/**
 * Solar Voyager — Main Entry Point
 * M3.2: VR far-scene re-architecture — single camera, km units, farRoot counter-translation
 */

import * as THREE from 'three';
import { XRControllerModelFactory } from 'three/addons/webxr/XRControllerModelFactory.js';
import {
  createRenderer, updateResolutionGovernor, getRendererStats,
  setContentCallbacks, getGovernorTier, isFbScalePending, onSessionStart,
} from './core/renderer.js';
import { createCabin } from './ship/interior.js';
import { updateGlassUniforms, toggleDebugTransparent } from './ship/glass.js';
import { createSimulator, updateSimulator } from './input/simulator.js';
import { initControllers, updateControllers } from './input/controllers.js';
import { initGrab, registerGrabbable, updateGrab, isHandOccupied } from './input/grab.js';
import { initLocomotion, updateLocomotion, getRigPosition, getCurrentSpeed, resetRigPosition } from './input/locomotion.js';
import { tickClock, getCurrentJD, setWarpFactor, getWarpFactor } from './sim/clock.js';
import {
  BODIES, type BodyId, PHYSICAL,
  getHeliocentricPosition,
} from './sim/ephemeris.js';
import {
  getFarScene, getFarRoot,
  setShipPosition, updateBodyMesh, updateFarRoot, getSunLightDir,
} from './space/farScene.js';
import { createCelestialBody, BODY_COLORS, createSunMesh } from './space/celestial.js';
import { createStarfield, createMilkyWayGlow } from './space/starfield.js';

// ── Rendering ─────────────────────────────────────────────────
const renderer = createRenderer();
renderer.toneMappingExposure = 1.0;
document.body.appendChild(renderer.domElement);

// ── Single camera for both passes ──────────────────────────────
// near=0.05 keeps cabin depth precise; far=1.5e7 km covers the solar system.
// Never change at runtime — changing them churns XR render state.
const camera = new THREE.PerspectiveCamera(70, window.innerWidth / window.innerHeight, 0.05, 1.5e7);
camera.position.set(0, 1.6, 0);

// ── Player rig ────────────────────────────────────────────────
const rig = new THREE.Group();
rig.position.set(0, 0, 1.5);

const nearScene = new THREE.Scene();
nearScene.background = null;
nearScene.add(rig);
rig.add(camera);  // camera is child of rig — rig moves, camera follows

// ── Lighting (near scene — sun-driven) ────────────────────────
nearScene.add(new THREE.HemisphereLight(0x8899bb, 0x222233, 0.6));
const dirLight = new THREE.DirectionalLight(0xfff4e0, 1.5);
dirLight.position.set(2, 3, 4); // overwritten each frame
nearScene.add(dirLight);

// ── Cabin ─────────────────────────────────────────────────────
const { cabin, glassMeshes } = createCabin();
nearScene.add(cabin);

// ── Far scene ─────────────────────────────────────────────────
const farScene = getFarScene();
const farRoot = getFarRoot(); // all far content parented under this

const sunLight = new THREE.DirectionalLight(0xffffff, 3.0);
farRoot.add(sunLight);
farRoot.add(new THREE.AmbientLight(0x112233, 0.3));

const starfield = createStarfield();
farRoot.add(starfield);
const milkyWay = createMilkyWayGlow();
farRoot.add(milkyWay);

// ── Celestial bodies ──────────────────────────────────────────
const bodyMeshes: Map<BodyId, THREE.Mesh> = new Map();

const { disc: sunDisc, glow: sunGlow } = createSunMesh();
farRoot.add(sunDisc);
farRoot.add(sunGlow);
bodyMeshes.set('sun', sunDisc);

for (const id of BODIES) {
  if (id === 'sun') continue;
  const body = createCelestialBody(id, BODY_COLORS[id]);
  farRoot.add(body.mesh);
  bodyMeshes.set(id, body.mesh);
}

// ── Governor content callbacks ─────────────────────────────────
setContentCallbacks(
  () => {
    for (const [, mesh] of bodyMeshes) {
      if (mesh.geometry.type === 'SphereGeometry') {
        const geo = mesh.geometry as THREE.SphereGeometry;
        const oldGeo = geo;
        const r = (oldGeo.parameters as any).radius || 1;
        mesh.geometry = new THREE.SphereGeometry(r, 24, 16);
        oldGeo.dispose();
      }
    }
    starfield.geometry.setDrawRange(0, 12000);
    starfield.geometry.attributes.position.needsUpdate = true;
    milkyWay.visible = false;
  },
  () => {
    for (const [, mesh] of bodyMeshes) {
      if (mesh.geometry.type === 'SphereGeometry') {
        const geo = mesh.geometry as THREE.SphereGeometry;
        const oldGeo = geo;
        const r = (oldGeo.parameters as any).radius || 1;
        mesh.geometry = new THREE.SphereGeometry(r, 48, 32);
        oldGeo.dispose();
      }
    }
    starfield.geometry.setDrawRange(0, 25000);
    starfield.geometry.attributes.position.needsUpdate = true;
    milkyWay.visible = true;
  },
);

// ── Ship position: sub-solar day side of Earth ────────────────
const bootJD = getCurrentJD();
const earthPos = getHeliocentricPosition('earth', bootJD);
const sunPosBoot = getHeliocentricPosition('sun', bootJD);
const earthRadiusKm = PHYSICAL['earth'].radiusKm;
const ALTITUDE_KM = 200;

const toSunX = sunPosBoot.x - earthPos.x;
const toSunY = sunPosBoot.y - earthPos.y;
const toSunZ = sunPosBoot.z - earthPos.z;
const toSunLen = Math.sqrt(toSunX * toSunX + toSunY * toSunY + toSunZ * toSunZ);
const tsNx = toSunX / toSunLen, tsNy = toSunY / toSunLen, tsNz = toSunZ / toSunLen;

const offA = 30 * (Math.PI / 180);
const cosO = Math.cos(offA), sinO = Math.sin(offA);
// orbital velocity direction: cross(sunDir, ecliptic north)
const vx = tsNy * 0 - tsNz * 0; // eclN = (0,0,1)
const vy = tsNz * 0 - tsNx * 0;
const vz = tsNx * 0 - tsNy * 1; // simplified: velocity ⊥ sunDir in ecliptic
const vl = Math.sqrt(vx * vx + vy * vy + vz * vz);
// Better: velocity is perpendicular to sunDir in ecliptic plane
const perpX = -tsNy, perpY = tsNx, perpZ = 0;
const pl = Math.sqrt(perpX * perpX + perpY * perpY);
const vnx = perpX / pl, vny = perpY / pl, vnz = 0;

const snx = tsNx * cosO + vnx * sinO;
const sny = tsNy * cosO + vny * sinO;
const snz = tsNz * cosO + vnz * sinO;
const sl = Math.sqrt(snx * snx + sny * sny + snz * snz);

const orbitR = earthRadiusKm + ALTITUDE_KM;
let shipX = earthPos.x + (snx / sl) * orbitR;
let shipY = earthPos.y + (sny / sl) * orbitR;
let shipZ = earthPos.z + (snz / sl) * orbitR;
let shipVx = 0, shipVy = 0, shipVz = 0;
setShipPosition(shipX, shipY, shipZ);

const dayCheck = ((shipX - earthPos.x) * tsNx + (shipY - earthPos.y) * tsNy + (shipZ - earthPos.z) * tsNz) / orbitR;
console.log(`[SPAWN] Day-side check: ${dayCheck.toFixed(3)} (expect >0.3)`);

// ── XR Setup ──────────────────────────────────────────────────
renderer.xr.enabled = true;
const ctrlFactory = new XRControllerModelFactory();
const controllers: THREE.Group[] = [];
for (let i = 0; i < 2; i++) {
  const grip = renderer.xr.getControllerGrip(i);
  grip.add(ctrlFactory.createControllerModel(grip));
  rig.add(grip);
  const ctrl = renderer.xr.getController(i);
  ctrl.addEventListener('connected', (e: any) => { ctrl.userData.handedness = e.data.handedness; });
  rig.add(ctrl);
  controllers.push(ctrl);
}

// ── Grab ──────────────────────────────────────────────────────
initGrab(controllers[0], renderer.xr.getControllerGrip(0), controllers[1], renderer.xr.getControllerGrip(1));
const yw = cabin.getObjectByName('yoke-wheel');
const th = cabin.getObjectByName('throttle-handle');
if (yw) registerGrabbable({ mesh: yw, id: 'yoke', grabPoint: new THREE.Vector3() });
if (th) registerGrabbable({ mesh: th, id: 'throttle', grabPoint: new THREE.Vector3() });
initLocomotion(rig, camera);
resetRigPosition();

// ── Dev HUD ───────────────────────────────────────────────────
let hudVisible = false;
let hudPanel: THREE.Group | null = null;
let hudTex: THREE.CanvasTexture | null = null;
let hudCanvas: HTMLCanvasElement | null = null;
let hudCtx: CanvasRenderingContext2D | null = null;
let glassVisible = true;

const _camWorldPos = new THREE.Vector3();
const _sunRel = new THREE.Vector3();
const _sunWorld = new THREE.Vector3();
const _frustum = new THREE.Frustum();
const _projMat = new THREE.Matrix4();
const _camFwd = new THREE.Vector3();
let _farDrawCalls = 0;
let _nearDrawCalls = 0;

function buildHUD(): THREE.Group {
  const canvas = document.createElement('canvas');
  canvas.width = 640;
  canvas.height = 520;
  hudCanvas = canvas;
  hudCtx = canvas.getContext('2d')!;
  hudTex = new THREE.CanvasTexture(canvas);
  hudTex.minFilter = THREE.LinearFilter;
  const m = new THREE.Mesh(new THREE.PlaneGeometry(1.28, 1.04),
    new THREE.MeshBasicMaterial({ map: hudTex, transparent: true, depthTest: false }));
  m.renderOrder = 999;
  const g = new THREE.Group(); g.add(m); g.visible = false;
  return g;
}

function updateHUD(dt: number, jd: number, sunPosKm: { x: number; y: number; z: number }) {
  if (!hudCtx || !hudCanvas || !hudTex) return;
  const ctx = hudCtx;
  ctx.clearRect(0, 0, 640, 520);
  ctx.fillStyle = 'rgba(0,0,0,0.82)';
  ctx.fillRect(0, 0, 640, 520);

  const stats = getRendererStats();
  const fps = dt > 0 ? (1000 / dt).toFixed(0) : '--';
  const rp = getRigPosition();
  const tier = getGovernorTier();
  const lo = isHandOccupied('left'), ro = isHandOccupied('right');
  const spd = getCurrentSpeed();

  ctx.fillStyle = '#0f0'; ctx.font = '13px monospace'; ctx.textAlign = 'left';
  const s = [
    `FPS:${fps}  Frame:${dt.toFixed(1)}ms  Gov:t${tier}${isFbScalePending() ? ' [next:.92]' : ''}`,
    `FAR:${_farDrawCalls} | NEAR:${_nearDrawCalls}  Tri:${(stats.triangles/1000).toFixed(0)}k  FB:${stats.fbScale.toFixed(2)}`,
    `Cam near:${camera.near.toFixed(2)}  far:${(camera.far/1e6).toFixed(1)}M  Rig:(${rp.x.toFixed(2)},${rp.z.toFixed(2)})  Spd:${spd.toFixed(1)}`,
    `Grab L:${lo?'Y':'-'} R:${ro?'Y':'-'}  Glass:${glassVisible?'ON':'OFF'}  Warp:x${getWarpFactor().toFixed(0)}`,
    `Ship:${(shipX/1e6).toFixed(2)}M km  Alt:${ALTITUDE_KM}km  JD:${jd.toFixed(2)}`,
  ];
  s.forEach((l, i) => ctx.fillText(l, 10, 18 + i * 16));

  // SUN DEBUG
  const sunRel = getSunLightDir(sunPosKm);
  _sunRel.copy(sunRel);
  camera.getWorldDirection(_camFwd);
  const bearing = Math.acos(Math.max(-1, Math.min(1, _camFwd.dot(_sunRel))));
  sunDisc.getWorldPosition(_sunWorld);
  const sd = _sunWorld.length();
  const sr = PHYSICAL['sun'].radiusKm;
  const angD = 2 * Math.atan(sr / sd) * 180 / Math.PI;

  ctx.fillStyle = '#ff0'; ctx.font = '12px monospace';
  const sy = 115;
  ctx.fillText(`Sun→Ship:(${sunRel.x.toFixed(3)},${sunRel.y.toFixed(3)},${sunRel.z.toFixed(3)})  bearing:${(bearing*180/Math.PI).toFixed(1)}°`, 10, sy);
  ctx.fillText(`Sun mesh: dist=${(sd/1e6).toFixed(2)}M km  radius=${sr}km  angDiam=${angD.toFixed(3)}°`, 10, sy + 16);

  // frustum check
  _projMat.multiplyMatrices(camera.projectionMatrix, camera.matrixWorldInverse);
  _frustum.setFromProjectionMatrix(_projMat);
  const inF = _frustum.containsPoint(_sunWorld);
  ctx.fillText(`Sun in frustum: ${inF?'YES':'NO'}`, 10, sy + 32);

  // farRoot position
  const frp = farRoot.position;
  ctx.fillText(`farRoot: (${frp.x.toFixed(1)}, ${frp.y.toFixed(1)}, ${frp.z.toFixed(1)})`, 10, sy + 48);

  // light match check (approx)
  const ld = new THREE.Vector3(); dirLight.getWorldDirection(ld);
  const dotC = Math.abs(ld.dot(_sunRel));
  ctx.fillStyle = dotC > 0.99 ? '#0f0' : '#f44';
  ctx.fillText(`Light≈sun: ${dotC>0.99?'YES':'NO'} (dot=${dotC.toFixed(4)})`, 10, sy + 64);

  hudTex.needsUpdate = true;
}

// ── VR Button ─────────────────────────────────────────────────
const vrButton = document.getElementById('vr-button') as HTMLButtonElement;

async function initXR() {
  const hx = !!navigator.xr;
  if (!hx) { vrButton.textContent = 'No XR'; vrButton.disabled = false; vrButton.onclick = () => { vrButton.style.display = 'none'; createSimulator(camera, nearScene); }; return; }
  let sup = false;
  try { sup = await navigator.xr!.isSessionSupported('immersive-vr'); } catch { /* */ }
  if (!sup) { vrButton.textContent = 'XR unsupported'; vrButton.disabled = false; vrButton.onclick = () => { vrButton.style.display = 'none'; createSimulator(camera, nearScene); }; return; }
  vrButton.textContent = 'Enter VR'; vrButton.disabled = false; vrButton.style.display = '';
  vrButton.onclick = async () => {
    vrButton.style.display = 'none';
    try {
      const s = await navigator.xr!.requestSession('immersive-vr', { requiredFeatures: ['local-floor'], optionalFeatures: ['hand-tracking', 'layers'] });
      renderer.xr.setSession(s);
    } catch (e: any) { console.error(e.message); vrButton.style.display = ''; }
  };
  renderer.xr.addEventListener('sessionstart', () => {
    const s = renderer.xr.getSession();
    if (s) s.updateTargetFrameRate?.(90).catch(() => {});
    initControllers(renderer, nearScene);
    onSessionStart();
    addHUDToCamera();
  });
  renderer.xr.addEventListener('sessionend', () => { removeHudFromCamera(); vrButton.style.display = ''; });
}

function addHUDToCamera() { if (!hudPanel) { hudPanel = buildHUD(); hudPanel.position.set(0, 0, -2); camera.add(hudPanel); hudPanel.visible = false; } }
function removeHudFromCamera() { if (hudPanel) { camera.remove(hudPanel); hudPanel = null; if (hudTex) hudTex.dispose(); hudTex = null; hudCanvas = null; hudCtx = null; } }

// ── Animation ─────────────────────────────────────────────────
let lastTime = 0;
let simMode = false;

function animate(timestamp: number, _xrFrame?: XRFrame) {
  const dt = lastTime ? Math.min(timestamp - lastTime, 100) : 16.67;
  lastTime = timestamp;
  const dtSec = dt / 1000;
  tickClock(dt);
  const jd = getCurrentJD();

  if (simMode) updateSimulator(dt, camera);

  const input = updateControllers(renderer);
  const gs = updateGrab(input);

  // Flight placeholder
  if (gs.leftOccupied || gs.rightOccupied) {
    if (gs.rightOccupied) {
      shipVy += input.rightStickY * 45 * (Math.PI/180) * dtSec * 10;
      shipVx += input.rightStickX * 30 * (Math.PI/180) * dtSec * 10;
    }
    if (gs.leftOccupied) {
      const t = input.leftStickY * 0.5 * dtSec;
      shipX += shipVx * dtSec + t * 0.1;
      shipY += shipVy * dtSec + t * 0.1;
      shipZ += shipVz * dtSec + t * 0.1;
    }
    shipVx *= 0.99; shipVy *= 0.99; shipVz *= 0.99;
    setShipPosition(shipX, shipY, shipZ);
  }

  updateLocomotion(dtSec, input, timestamp);
  updateResolutionGovernor(dt);

  // ── Far scene update ───────────────────────────────
  const sunPosKm = getHeliocentricPosition('sun', jd);
  for (const id of BODIES) {
    const m = bodyMeshes.get(id);
    if (!m) continue;
    updateBodyMesh(m, getHeliocentricPosition(id, jd), PHYSICAL[id].radiusKm);
  }
  sunGlow.position.copy(sunDisc.position);

  const sunRel = getSunLightDir(sunPosKm);
  sunLight.position.copy(sunRel.clone().multiplyScalar(1e9));
  dirLight.position.copy(sunRel.clone().multiplyScalar(10));
  updateGlassUniforms(sunRel);

  // Counter-translate farRoot by camera world position
  if (renderer.xr.isPresenting) {
    renderer.xr.getCamera().getWorldPosition(_camWorldPos);
  } else {
    camera.getWorldPosition(_camWorldPos);
  }
  updateFarRoot(_camWorldPos);

  // ── HUD ────────────────────────────────────────────
  updateHUD(dt, jd, sunPosKm);
  if (hudPanel) hudPanel.visible = hudVisible;

  // ── Two-pass render ────────────────────────────────
  // Pass 1: Far scene (rotation-only, farRoot counter-translated)
  renderer.autoClear = false;
  renderer.clear();
  renderer.render(farScene, camera);
  _farDrawCalls = renderer.info.render.calls;

  // Pass 2: Near scene (cabin)
  renderer.clearDepth();
  renderer.render(nearScene, camera);
  _nearDrawCalls = renderer.info.render.calls - _farDrawCalls;
}

renderer.setAnimationLoop(animate);

window.addEventListener('resize', () => {
  const a = window.innerWidth / window.innerHeight;
  camera.aspect = a; camera.updateProjectionMatrix();
});

// ── Keyboard ──────────────────────────────────────────────────
let _wireframe = false;
window.addEventListener('keydown', (e) => {
  const k = e.key.toLowerCase();
  if (k === 'h') hudVisible = !hudVisible;
  if (k === 'g') {
    _wireframe = !_wireframe;
    cabin.traverse(c => {
      if ((c as THREE.Mesh).isMesh) {
        const m = (c as THREE.Mesh).material;
        for (const mm of (Array.isArray(m) ? m : [m])) {
          if (mm && 'wireframe' in mm) (mm as THREE.MeshBasicMaterial).wireframe = _wireframe;
        }
      }
    });
  }
  if (k === 'l') { glassVisible = !glassVisible; glassMeshes.forEach(g => g.visible = glassVisible); }
  if (k === 'k') toggleDebugTransparent();
  if (k === 'e' && !renderer.xr.isPresenting && !simMode) {
    simMode = true; createSimulator(camera, nearScene); vrButton.style.display = 'none';
  }
  if (k === 'n') {
    const np = getHeliocentricPosition('neptune', getCurrentJD());
    const nr = PHYSICAL['neptune'].radiusKm;
    shipX = np.x + nr + 200; shipY = np.y; shipZ = np.z; shipVx = shipVy = shipVz = 0;
    setShipPosition(shipX, shipY, shipZ);
  }
  if (k === ']') setWarpFactor(getWarpFactor() * 10);
  if (k === '[') setWarpFactor(Math.max(1, getWarpFactor() / 10));
});

// ── Boot ───────────────────────────────────────────────────────
async function boot() {
  console.log('[BOOT] Solar Voyager M3.2');
  console.log(`[CAM] near=${camera.near} far=${camera.far} (km units)`);
  await initXR();
  console.log('[BOOT] H=HUD G=wire L=glass K=glassDbg N=Neptune');
}
boot().catch(console.error);

export { camera, nearScene as scene, renderer };
